import React, { useEffect, useState } from "react";
import student from "../../assets/student.png";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import axios from "axios";
import { RiMedal2Line, RiCheckboxCircleLine } from "react-icons/ri";

const StatPill = ({ Icon, value, label, mobileLine1, mobileLine2 }) => {
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 shadow-sm">
      <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80">
        <Icon size={18} />
      </div>

      <div className="min-w-0 text-center">
        <div className="text-lg font-semibold text-white tabular-nums leading-none py-1">
          {value}
        </div>

        <div className="text-[11px] leading-tight text-white/70">
          {/* Mobile: force 2 lines so it never overflows */}
          <span className="sm:hidden block">{mobileLine1}</span>
          <span className="sm:hidden block">{mobileLine2}</span>

          {/* Sm+: normal label */}
          <span className="hidden sm:inline">{label}</span>
        </div>
      </div>
    </div>
  );
};

const UserDashboardHeader = () => {
  const location = useLocation();
  const isUserDashboard = location.pathname === "/user-dashboard";
  const userInfo = JSON.parse(localStorage.getItem("user") || "{}");

  const [subjectsMastered, setSubjectsMastered] = useState(0);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);

  const capitalizeFirstLetter = (string) => {
    if (string) return string.charAt(0).toUpperCase() + string.slice(1);
    return "";
  };

  useEffect(() => {
    const run = async () => {
      if (!userInfo?.id) return;

      try {
        const enrollResp = await axios.get(`${API_BASE_URL}/enrollments`);
        const enrollments = (enrollResp.data || []).filter(
          (e) => e.student_id === userInfo.id
        );

        const subjectIds = [...new Set(enrollments.map((e) => e.subject_id))];

        if (subjectIds.length === 0) {
          setSubjectsMastered(0);
          setQuestionsCompleted(0);
          return;
        }

        const stats = await Promise.all(
          subjectIds.map(async (subjectId) => {
            try {
              const resp = await axios.get(
                `${API_BASE_URL}/api/subject_mastery_statistics`,
                { params: { student_id: userInfo.id, subject_id: subjectId } }
              );
              return resp.data;
            } catch {
              return null;
            }
          })
        );

        const masteredCount = stats.filter((s) => {
          if (!s?.total_modules) return false;
          return s.mastered_modules === s.total_modules;
        }).length;

        const questionsTotal = stats.reduce(
          (sum, s) => sum + (s?.attempted_questions || 0),
          0
        );

        setSubjectsMastered(masteredCount);
        setQuestionsCompleted(questionsTotal);
      } catch {
        setSubjectsMastered(0);
        setQuestionsCompleted(0);
      }
    };

    run();
  }, [userInfo?.id]);

  return (
    <div className="w-full border-b border-white/10 bg-white/20 backdrop-blur-md">
      <div className="px-4 sm:px-6 lg:px-8 md:py-10 py-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Left: Welcome */}
          <div className="flex min-w-0 items-center gap-3">
            <img
              className="md:h-14 md:w-14 h-11 w-11 rounded-full ring-2 ring-white bg-white/10 p-1 invert brightness-0"
              src={student}
              alt="Student avatar"
            />

            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                Student Dashboard
              </p>

              <p className="text-xl sm:text-2xl font-thin text-white truncate">
                Welcome, {capitalizeFirstLetter(userInfo?.first_name)}
              </p>
            </div>
          </div>

          {/* Right: Stats (grid on mobile so labels never spill) */}
          <div className="grid grid-cols-2 gap-3 sm:flex sm:gap-4 sm:justify-end">
            <StatPill
              Icon={RiMedal2Line}
              value={subjectsMastered}
              label="Subjects Mastered"
              mobileLine1="Subjects"
              mobileLine2="Mastered"
            />
            <StatPill
              Icon={RiCheckboxCircleLine}
              value={questionsCompleted}
              label="Questions Completed"
              mobileLine1="Questions"
              mobileLine2="Completed"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboardHeader;
